import { Component, HostListener, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CdkDragDrop } from '@angular/cdk/drag-drop';

export interface PanelContentBlock {
  id: string;
  type: 'display' | 'text' | 'listItem' | 'buttons' | 'contactField' | 'address' | 'videoLink' | 'branding' | 'socialLinks' | 'collections';
  label: string;
  subLabel?: string;
  data?: any;
}

@Component({
  selector: 'app-page-structure-panel',
  templateUrl: './page-structure-panel.component.html',
  styleUrls: ['./page-structure-panel.component.scss']
})
export class PageStructurePanelComponent implements OnChanges {
  @Input() templatePage: any[] = [];
  @Input() currentPageIndex: number = 0;

  @Output() sectionSelected    = new EventEmitter<{ section: any }>();
  @Output() sectionReordered   = new EventEmitter<{ previousIndex: number; currentIndex: number }>();
  @Output() sectionDuplicated  = new EventEmitter<{ section: any; index: number }>();
  @Output() sectionDeleted     = new EventEmitter<{ index: number }>();
  @Output() addSectionRequested = new EventEmitter<{ position: 'ABOVE' | 'BELOW'; panelIndex: number }>();
  @Output() blockSelected      = new EventEmitter<{ section: any; block: PanelContentBlock }>();
  @Output() headerFeatureToggled = new EventEmitter<{ feature: 'movingText' | 'countdownBanner'; enabled: boolean }>();

  selectedSectionId: string = '';
  selectedBlockId: string = '';
  hoveredSectionIndex: number = -1;
  expandedSectionIds = new Set<string>();
  showHeaderAddMenu = false;
  private cachedBlocks = new Map<string, PanelContentBlock[]>();

  ngOnChanges(_changes: SimpleChanges): void {
    this.selectedSectionId = '';
    this.selectedBlockId = '';
    this.cachedBlocks.clear();
  }

  get currentPage(): any {
    return this.templatePage?.[this.currentPageIndex];
  }

  get isHomePage(): boolean {
    return this.currentPage?.pageName === 'Home';
  }

  get headerSection(): any {
    return this.templatePage?.[0]?.components?.[0] ?? null;
  }

  get footerSection(): any {
    const comps: any[] = this.templatePage?.[0]?.components ?? [];
    return comps.length > 1 ? comps[comps.length - 1] : null;
  }

  get templateSections(): any[] {
    if (!this.currentPage) return [];
    const comps: any[] = this.currentPage.components ?? [];
    if (this.isHomePage) {
      return comps.length > 2 ? comps.slice(1, comps.length - 1) : [];
    }
    return comps;
  }

  get isEmpty(): boolean {
    return this.templateSections.length === 0;
  }

  // ── Header feature helpers ────────────────────────────────

  get isMovingTextEnabled(): boolean {
    return this.headerSection?.styles?.headline?.display === true;
  }

  get isCountdownBannerEnabled(): boolean {
    return this.headerSection?.styles?.countdown?.display === true;
  }

  get hasAllHeaderFeatures(): boolean {
    return this.isMovingTextEnabled && this.isCountdownBannerEnabled;
  }

  toggleHeaderAddMenu(e: MouseEvent): void {
    e.stopPropagation();
    this.showHeaderAddMenu = !this.showHeaderAddMenu;
  }

  addHeaderFeature(e: MouseEvent, feature: 'movingText' | 'countdownBanner'): void {
    e.stopPropagation();
    this.showHeaderAddMenu = false;
    this.headerFeatureToggled.emit({ feature, enabled: true });
  }

  removeHeaderFeature(e: MouseEvent, feature: 'movingText' | 'countdownBanner'): void {
    e.stopPropagation();
    this.headerFeatureToggled.emit({ feature, enabled: false });
  }

  @HostListener('document:click')
  closeHeaderAddMenu(): void {
    this.showHeaderAddMenu = false;
  }

  // ── Section row interactions ──────────────────────────────

  onSectionClick(section: any): void {
    if (!section) return;
    this.selectedSectionId = section.id;
    this.selectedBlockId = '';
    this.sectionSelected.emit({ section });
  }

  onDrop(event: CdkDragDrop<any[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    this.sectionReordered.emit({
      previousIndex: event.previousIndex,
      currentIndex: event.currentIndex
    });
  }

  onDuplicateSection(event: MouseEvent, section: any, index: number): void {
    event.stopPropagation();
    this.sectionDuplicated.emit({ section, index });
  }

  onDeleteSection(event: MouseEvent, index: number): void {
    event.stopPropagation();
    this.sectionDeleted.emit({ index });
  }

  onAddSection(position: 'ABOVE' | 'BELOW', panelIndex: number): void {
    this.addSectionRequested.emit({ position, panelIndex });
  }

  // ── Expand / collapse ─────────────────────────────────────

  toggleExpand(e: MouseEvent, sectionId: string): void {
    e.stopPropagation();
    if (this.expandedSectionIds.has(sectionId)) {
      this.expandedSectionIds.delete(sectionId);
    } else {
      this.expandedSectionIds.add(sectionId);
    }
  }

  isExpanded(sectionId: string): boolean {
    return this.expandedSectionIds.has(sectionId);
  }

  // ── Content block derivation ──────────────────────────────

  getContentBlocks(section: any): PanelContentBlock[] {
    const id = section?.id;
    if (!id) return [];
    if (!this.cachedBlocks.has(id)) {
      this.cachedBlocks.set(id, this.computeContentBlocks(section));
    }
    return this.cachedBlocks.get(id)!;
  }

  private computeContentBlocks(section: any): PanelContentBlock[] {
    const blocks: PanelContentBlock[] = [];
    const content = section?.content;
    const action  = section?.action;

    if (!content) return blocks;

    if (content.display) {
      blocks.push({ id: 'display', type: 'display', label: 'Display' });
    }

    if (content.inputText?.length > 0) {
      content.inputText.forEach((item: any, i: number) => {
        const raw = this.stripHtml(item.value || '');
        blocks.push({
          id: `text_${i}`,
          type: 'text',
          label: item.label || 'Text',
          subLabel: raw ? (raw.length > 30 ? raw.slice(0, 30) + '…' : raw) : undefined,
          data: item
        });
      });
    }

    if (content.listItem) {
      const count = content.listItem.data?.length ?? 0;
      blocks.push({
        id: 'listItem',
        type: 'listItem',
        label: content.listItem.label || 'List Items',
        subLabel: `${count} item${count !== 1 ? 's' : ''}`
      });
    }

    if (action?.buttons || action?.display !== undefined) {
      const count = action?.buttons?.length ?? 0;
      blocks.push({
        id: 'buttons',
        type: 'buttons',
        label: 'Buttons',
        subLabel: count > 0 ? `${count} button${count !== 1 ? 's' : ''}` : undefined
      });
    }

    if (content.contactField) {
      blocks.push({ id: 'contactField', type: 'contactField', label: 'Form Fields' });
    }

    if (content.address) {
      const name = content.address.name;
      blocks.push({
        id: 'address',
        type: 'address',
        label: 'Address',
        subLabel: name ? (name.length > 30 ? name.slice(0, 30) + '…' : name) : undefined
      });
    }

    if (content.videoLink) {
      blocks.push({ id: 'videoLink', type: 'videoLink', label: 'Video Link' });
    }

    if (content.siteName || content.logo || content.siteNameAndLogo) {
      blocks.push({ id: 'branding', type: 'branding', label: 'Branding' });
    }

    if (content.collectionId || content.collectionIds || content.categoriesIds) {
      blocks.push({ id: 'collections', type: 'collections', label: 'Collections' });
    }

    if (content.socialLinks) {
      const count = content.socialLinks.channels?.length ?? 0;
      blocks.push({
        id: 'socialLinks',
        type: 'socialLinks',
        label: 'Social Links',
        subLabel: count > 0 ? `${count} link${count !== 1 ? 's' : ''}` : undefined
      });
    }

    return blocks;
  }

  private stripHtml(html: string): string {
    if (!html) return '';
    return html.replace(/(<([^>]+)>)/gi, '').replace(/ /g, ' ').trim();
  }

  // ── Block click ───────────────────────────────────────────

  onBlockClick(e: MouseEvent, section: any, block: PanelContentBlock): void {
    e.stopPropagation();
    this.selectedSectionId = section.id;
    this.selectedBlockId = block.id;
    this.sectionSelected.emit({ section });
    this.blockSelected.emit({ section, block });
  }

  // ── Icon helpers ──────────────────────────────────────────

  getSectionIcon(sectionType: string): string {
    if (!sectionType) return 'default';
    const t = sectionType.toLowerCase();
    if (t.includes('header')) return 'header';
    if (t.includes('footer')) return 'footer';
    if (t.includes('carouselbanner') || t.includes('carousel')) return 'carousel';
    if (t.includes('banner') || t.includes('hero')) return 'banner';
    if (t.includes('video')) return 'video';
    if (t.includes('contact')) return 'contact';
    if (t.includes('testimonial')) return 'testimonial';
    if (t.includes('faq')) return 'faq';
    if (t.includes('pricing')) return 'pricing';
    if (t.includes('location') || t.includes('map')) return 'location';
    if (t.includes('team') || t.includes('member')) return 'team';
    if (t.includes('blog')) return 'blog';
    if (t.includes('ecommerce') || t.includes('product') || t.includes('shop') || t.includes('store')) return 'shop';
    if (t.includes('image') || t.includes('gallery') || t.includes('imagegrid')) return 'image';
    if (t.includes('service') || t.includes('feature') || t.includes('choose')) return 'grid';
    if (t.includes('text') || t.includes('heading') || t.includes('process')) return 'text';
    if (t.includes('logo')) return 'logo';
    return 'default';
  }

  getBlockIcon(type: PanelContentBlock['type']): string {
    switch (type) {
      case 'display':     return 'toggle';
      case 'text':        return 'text';
      case 'listItem':    return 'list';
      case 'buttons':     return 'button';
      case 'contactField':return 'form';
      case 'address':     return 'location';
      case 'videoLink':   return 'video';
      case 'branding':    return 'brand';
      case 'socialLinks': return 'social';
      case 'collections': return 'grid';
      default:            return 'default';
    }
  }

  displayName(section: any): string {
    return section?.sectionName || section?.sectionType || 'Section';
  }
}
